import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { LoadingButton } from '@mui/lab';
import { useMemo, useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CloseIconButton from '../../../../reusable-components/basic-buttons/CloseIconButton';
import Iconify from '../../../../reusable-components/iconify';
import { AwaitButton } from '../../../../reusable-components/await-button';
import CustomDataGrid from '../../../../reusable-components/datagrid/CustomDataGrid';
import { getAiUsageSites, postAiUsageReport, postAiUsageReportDownload, getCompaniesAndFacilitiesForReports } from '../../../../api/admin-reports';
import SimpleAutocompleteProps from '../../../../reusable-components/autocomplete/SimpleAutocompleteProps';
import { download } from '../../../../utils/download';
import {
  setSites,
  setCompaniesAndFacilities,
  setShowAllSites,
  setShowAllGroups,
  setShowAllFacilities,
  setOpenDialog,
  setDownloadingReportResults,
  setReport,
} from '../../../../redux/slices/adminReports';

const getDefaultOptions = () => {
  const today = new Date();
  const last30 = new Date();
  last30.setDate(today.getDate() - 29);

  return {
    startDate: last30.toISOString().split('T')[0],
    endDate: today.toISOString().split('T')[0],
    sites: [],
    companies: [],
    facilities: [],
    endpoints: []
  };
};

export default function ReportsView() {
  const dispatch = useDispatch();
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [dialogStep, setDialogStep] = useState(0);
  const [selectedReportType, setSelectedReportType] = useState(null);
  const [options, setOptions] = useState(() => getDefaultOptions());
  const {
    sites,
    facilities,
    companies,
    showAllSites,
    showAllGroups,
    showAllFacilities,
    openDialog,
    downloadingReportResults,
    report,
  } = useSelector((state) => state.adminReports.data);

  useEffect(() => {
    let isMounted = true;

    Promise.all([getAiUsageSites(), getCompaniesAndFacilitiesForReports()])
      .then(([siteResponse, companyFacilityResponse]) => {
        if (!isMounted) return;

        dispatch(setSites(siteResponse || []));
        dispatch(setCompaniesAndFacilities({
          companies: companyFacilityResponse?.companies || [],
          facilities: companyFacilityResponse?.facilities || [],
        }));
      });

    return () => {
      isMounted = false;
    };
  }, [dispatch]);

  const updateReportOptions = (optionUpdates) => {
    setOptions((prev) => ({
      ...prev,
      ...optionUpdates,
    }));
  };

  const renderTags = (value, getTagProps, showAll, setShowAll) => {
    const displayedValues = showAll ? value : value.slice(0, 3);
    return (
      <>
        {displayedValues.map((option, index) => (
          <Chip key={option.value} label={option.label} {...getTagProps({ index })} sx={{ mr: 0.5 }} />
        ))}
        {value.length > 3 && (
          <IconButton
            onClick={() => {
              setShowAll(prev => !prev);
            }}
            size="small"
            sx={{ ml: 0.5 }}
          >
            {showAll ? (
              <ExpandLessIcon />
            ) : (
              <>
                {' '}
                + {value.length - 3}
                <ExpandMoreIcon />
              </>
            )}
          </IconButton>
        )}
      </>
    );
  };

  const companiesOptions = useMemo(() => companies.map(company => ({
    value: company.id,
    label: company.name,
    id: company.id,
    siteId: company.siteId
  })), [companies]);

  const sitesOptions = useMemo(() => sites.map(site => ({
    value: site.value,
    label: site.label,
    id: site.value
  })), [sites]);

  const facilitiesOptions = useMemo(() => facilities.map(facility => ({
    value: facility.id,
    label: facility.facilityName,
    id: facility.id,
    companyId: facility.companyId,
    siteId: facility.siteId
  })), [facilities]);

  const filteredCompaniesOptions = useMemo(() => {
    if (!options.sites?.length) {
      return companiesOptions;
    }

    const selectedSiteIds = new Set(options.sites.map((site) => site.value));
    return companiesOptions.filter((company) =>
      !company.siteId || selectedSiteIds.has(company.siteId)
    );
  }, [companiesOptions, options.sites]);

  const filteredFacilitiesOptions = useMemo(() => {
    let filtered = facilitiesOptions;
    if (options.sites?.length) {
      const selectedSiteIds = new Set(options.sites.map((site) => site.value));
      filtered = filtered.filter((facility) =>
        !facility.siteId || selectedSiteIds.has(facility.siteId)
      );
    }

    if (options.companies?.length) {
      const selectedCompanyIds = new Set(options.companies.map((company) => company.value));
      filtered = filtered.filter((facility) => selectedCompanyIds.has(facility.companyId));
    }

    return filtered;
  }, [facilitiesOptions, options.sites, options.companies]);


  const autoCompleteDefaults = (options, values, showAllTags, setShowAllTags, onChange) => ({
    ...SimpleAutocompleteProps,
    multiple: true,
    disableCloseOnSelect: true,
    sx: { mt: 1 },
    options: [{ label: 'Select All', value: 'All' }, ...(options || [])],
    autoHighlight: true,
    value: values || [],
    getOptionKey: (opt) => opt?.value,
    onChange: (event, newValue) => {
      const newValueArray = newValue || [];
      onChange(
        newValueArray.some((option) => option.value === 'All')
          ? newValueArray.length - 1 === options?.length
            ? []
            : options
          : newValueArray
      );
    },
    renderTags: (value, getTagProps) => renderTags(value, getTagProps, showAllTags, setShowAllTags),
  });

  const reportTypeOptions = useMemo(() => ([
    { value: 'ai-cost', label: 'AI Cost', preview: 'Tracks how much each AI feature costs' }
  ]), []);

  const openNewReportDialog = () => {
    setSelectedReportType(null);
    setDialogStep(0);
    setOptions(getDefaultOptions());
    dispatch(setShowAllSites(false));
    dispatch(setShowAllGroups(false));
    dispatch(setShowAllFacilities(false));
    dispatch(setOpenDialog(true));
  };

  const runReport = async () => {
    setIsGeneratingReport(true);

    try {
      const payload = {
        ...options,
        sites: options.sites.map(s => s.value),
        groups: options.companies.map(c => c.value),
        facilities: options.facilities.map(f => f.value),
        siteLabels: options.sites.map(s => s.label),
        groupLabels: options.companies.map(c => c.label),
        facilityLabels: options.facilities.map(f => f.label),
        siteLabelsText: options.sites.length ? options.sites.map(s => s.label).join('; ') : 'All',
        groupLabelsText: options.companies.length ? options.companies.map(c => c.label).join('; ') : 'All',
        facilityLabelsText: options.facilities.length ? options.facilities.map(f => f.label).join('; ') : 'All',
      };

      const response = await postAiUsageReport(payload);
      dispatch(setReport(response.data));
    } finally {
      setIsGeneratingReport(false);
      dispatch(setOpenDialog(false));
      setDialogStep(0);
      setSelectedReportType(null);
    }
  };

  const handleReportDialogClose = () => {
    if (isGeneratingReport) {
      return;
    }
    dispatch(setOpenDialog(false));
    setDialogStep(0);
    setSelectedReportType(null);
  };

  const totalCalls = useMemo(() => {
    return (report?.results || []).reduce((sum, x) => sum + (x.calls ?? 0), 0);
  }, [report?.results]);

  const totalTokens = useMemo(() => {
    return (report?.results || []).reduce((sum, x) => sum + (x.tokens ?? 0), 0);
  }, [report?.results]);

  const totalPages = useMemo(() => {
    return (report?.results || []).reduce((sum, x) => sum + (x.pages ?? 0), 0);
  }, [report?.results]);

  const hasRunReport = report !== null;
  const getReportResults = async () => {

    dispatch(setDownloadingReportResults(true));

    try {
      const payload = {
        ...options,
        sites: options.sites.map(s => s.value),
        groups: options.companies.map(c => c.value),
        facilities: options.facilities.map(f => f.value),
        siteLabels: options.sites.map(s => s.label),
        groupLabels: options.companies.map(c => c.label),
        facilityLabels: options.facilities.map(f => f.label),
      };

      const response = await postAiUsageReportDownload(payload);
      const fileStart = options?.startDate || 'start';
      const fileEnd = options?.endDate || 'end';
      download(response.data, `ai-usage-report-${fileStart}-to-${fileEnd}.xlsx`);
    } finally {
      dispatch(setDownloadingReportResults(false));
    }
  };



  const columns = useMemo(() => [
    {
      field: 'site',
      headerName: 'Site',
      flex: 1,
      filterable: true,
      sortable: true
    },
    {
      field: 'endpoint',
      headerName: 'Feature',
      flex: 1,
      filterable: true,
      sortable: true
    },
    {
      field: 'calls',
      headerName: 'Calls',
      type: 'number',
      filterable: true,
      sortable: true
    },
    {
      field: 'tokens',
      headerName: 'Tokens',
      type: 'number',
      filterable: true,
      sortable: true
    },
    {
      field: 'pages',
      headerName: 'Pages',
      type: 'number',
      filterable: true,
      sortable: true
    },
    {
      field: 'cost',
      headerName: 'Total Cost',
      type: 'number',
      filterable: true,
      sortable: true,
      renderCell: (params) => `$${Number(params.value ?? 0).toFixed(2)}`
    },
    {
      field: 'avgCostPerCall',
      headerName: 'Avg Cost Per Call',
      type: 'number',
      filterable: true,
      sortable: true,
      renderCell: (params) => (params.value == null ? '-' : `$${Number(params.value).toFixed(4)}`)
    }
  ], []);

  const tableRows = useMemo(() => {
    return (report?.results || []).map((x, index) => ({
      id: `${x.site ?? ''}-${x.endpoint ?? ''}-${index}`,
      site: x.site ?? '',
      endpoint: x.endpoint ?? '',
      calls: x.calls ?? 0,
      tokens: x.tokens ?? 0,
      pages: x.pages ?? 0,
      cost: x.cost ?? 0,
      avgCostPerCall: (x.calls ?? 0) > 0 ? Number(x.cost ?? 0) / Number(x.calls ?? 0) : 0,
    }));
  }, [report?.results]);

  const pinnedRows = useMemo(() => {
    if (!hasRunReport) {
      return undefined;
    }

    return {
      top: [
        {
          id: 'total-row',
          site: 'Total',
          endpoint: 'Total',
          calls: totalCalls,
          tokens: totalTokens,
          pages: totalPages,
          cost: report?.totalCost ?? 0,
          avgCostPerCall: totalCalls > 0 ? Number(report?.totalCost ?? 0) / totalCalls : 0,
        }
      ]
    };
  }, [hasRunReport, totalCalls, totalTokens, totalPages, report?.totalCost]);

  const tableToolbar = useCallback(
    () => (
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 1 }}>
        <Button variant="outlined" size="small" color="secondary" onClick={openNewReportDialog}>
          New Report
        </Button>
        {hasRunReport && (
          <LoadingButton
            color="secondary"
            variant="outlined"
            loading={downloadingReportResults}
            size="small"
            onClick={getReportResults}
            startIcon={<Iconify icon={'mdi:tray-arrow-down'} />}
          >
            Download
          </LoadingButton>
        )}
      </Box>
    ),
    [hasRunReport, downloadingReportResults]
  );

  return (
    <Box sx={{ width: '100%', minHeight: 'calc(100vh - 250px)' }}>
      <Dialog
        open={openDialog}
        onClose={(event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          handleReportDialogClose();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: 'center', position: 'relative' }}>
          {dialogStep === 0 ? 'Choose Report Type' : 'New Report Options'}
          <CloseIconButton onClick={handleReportDialogClose}/>
        </DialogTitle>
        <DialogContent>
          {dialogStep === 0 && (
            <Box sx={{ mt: 1, mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Report Type</Typography>
              <Autocomplete
                {...SimpleAutocompleteProps}
                autoHighlight
                options={reportTypeOptions}
                value={selectedReportType}
                onChange={(event, newValue) => setSelectedReportType(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Choose report type..."
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" sx={{ display: 'flex', alignItems: 'center' }} {...props}>
                    {option.label}
                    <Tooltip title={option.preview}>
                      <IconButton>
                        <Iconify icon={'eva:eye-outline'} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
              />
            </Box>
          )}

          {dialogStep === 1 && (
            <Box sx={{ mt: 1, mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Date Range</Typography>
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <TextField
                  label="Start Date"
                  type="date"
                  value={options.startDate || ''}
                  onChange={e => updateReportOptions({ startDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  sx={{ flex: 1 }}
                />
                <TextField
                  label="End Date"
                  type="date"
                  value={options.endDate || ''}
                  onChange={e => updateReportOptions({ endDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  sx={{ flex: 1 }}
                />
              </Box>

              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>Sites:</Typography>
              <Autocomplete
                {...autoCompleteDefaults(
                  sitesOptions,
                  options.sites,
                  showAllSites,
                  (value) => dispatch(setShowAllSites(value)),
                  (newValues) => {
                    updateReportOptions({
                      sites: newValues,
                      companies: [],
                      facilities: []
                    });
                  }
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select sites..."
                  />
                )}
              />

              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>Groups:</Typography>
              <Autocomplete
                {...autoCompleteDefaults(
                  filteredCompaniesOptions,
                  options.companies,
                  showAllGroups,
                  (value) => dispatch(setShowAllGroups(value)),
                  (newValues) => {
                    updateReportOptions({
                      companies: newValues,
                      facilities: []
                    });
                  }
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select groups..."

                  />
                )}
              />

              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mt: 2, mb: 1 }}>Facilities:</Typography>
              <Autocomplete
                {...autoCompleteDefaults(
                  filteredFacilitiesOptions,
                  options.facilities,
                  showAllFacilities,
                  (value) => dispatch(setShowAllFacilities(value)),
                  (newValues) => {
                    updateReportOptions({
                      facilities: newValues
                    });
                  }
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select facilities..."
                  />
                )}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleReportDialogClose} color="secondary" disabled={isGeneratingReport}>Cancel</Button>
          {dialogStep === 0 && (
            <Button variant="contained" color="primary" disabled={!selectedReportType} onClick={() => setDialogStep(1)}>
              Next
            </Button>
          )}
          {dialogStep === 1 && (
            <>
              <Button variant="outlined" color="secondary" onClick={() => setDialogStep(0)} disabled={isGeneratingReport}>
                Back
              </Button>
              <AwaitButton onClick={runReport} variant="contained" color="primary" loading={isGeneratingReport} disabled={isGeneratingReport}>Generate Report</AwaitButton>
            </>
          )}
        </DialogActions>
      </Dialog>

      <CustomDataGrid
        gridId="ai-usage-reports"
        boxSX={{ height: 'calc(100vh - 250px)', width: '100%' }}
        data={tableRows}
        pinnedRows={pinnedRows}
        gridColumns={columns}
        CustomLeftToolbar={tableToolbar}
        getRowId={(row) => row.id}
        isLoading={isGeneratingReport}
      />
    </Box>
  );
}